import { Component, EventEmitter, Input, OnDestroy, OnInit, Output } from '@angular/core';
import { AnyObject, ErrorMessage, FieldOption } from '@mri/types';
import { delay, filter, finalize, map, mergeMap, Observable, of, Subject, Subscription, tap } from 'rxjs';
import { catchError } from 'rxjs/operators';
import { ConfigResourceService, GenericResourceService } from '@mri/app/resources';

const waitPress = 100;


@Component({
  selector: 'app-field-select',
  styleUrl: './field-select.component.scss',
  templateUrl: './field-select.component.html'
})
export class FieldSelectComponent implements OnInit, OnDestroy {
  private _optionConfigKey$ = new Subject<string>();
  private _findInvert$ = new Subject<string>();
  private _search$ = new Subject<string>();
  private _subscription = new Subscription();
  private _classSet: Set<string> = new Set<string>();
  private _lastPressTime = new Date();
  private _options: FieldOption[] = [];
  private _optionConfigKey?: string;
  private _lastSearch = '';

  optionList: FieldOption[];
  labelSelected?: string;
  valueSelected?: string;
  imageSelected?: string;
  isLoading: boolean;

  @Input() errors?: Array<ErrorMessage>;
  @Input() label: string;
  @Input() optionsUrl?: string;
  @Input() sizeResults: number = 10;
  @Input() optionsUrlInvert?: string;
  @Input() invertUrl?: string;
  @Input() placeholder?: string;
  @Input() filter?: Record<string, string | string[]>;

  @Input() set optionConfigKey(value: string) {
    if (!value) return;

    this._optionConfigKey = value;
    this._optionConfigKey$.next(value);
  }

  @Input() set options(options: FieldOption[]) {
    this._options = options;
    this.valueSelected && this._showOptions(this.valueSelected);
  }

  @Input() set value(value: string) {
    if (this.valueSelected === value) return;

    this.valueSelected = value;
    this._setOptionValue(value);
  }

  @Output() valueChange = new EventEmitter<string>();

  get value(): string | undefined {
    return this.valueSelected;
  }

  get options(): FieldOption[] {
    return this._options;
  }

  constructor(
    private _genericResource: GenericResourceService,
    private _configResource: ConfigResourceService
  ) {
  }

  ngOnDestroy(): void {
    this._subscription.unsubscribe();
  }

  ngOnInit(): void {
    this._subscription.add(
      this._search$
        .pipe(
          delay(waitPress),
          filter(() => this._checkTimePress()),
          mergeMap(search => this._consultOptions(search))
        )
        .subscribe()
    );

    this._subscription.add(
      this._findInvert$
        .pipe(
          mergeMap(search => this._findOptionFrom(search)),
          filter(option => !!option),
          tap(option => {
            if (!option) return;

            const { label, value, image } = option;
            this.labelSelected = label;
            this.valueSelected = value;
            this.imageSelected = image;
          })
        ).subscribe()
    );


    this._subscription.add(
      this._optionConfigKey$
        .pipe(
          mergeMap(fieldKey => this._configResource.getOptions$(fieldKey)),
          filter(option => !!option),
          tap(option => this._options = [...(this._options || []), ...option]),
          tap(() => this.valueSelected && this._setOptionValue(this.valueSelected))
        ).subscribe()
    );


    if (this.valueSelected) this._setOptionValue(this.valueSelected);

    if (this._optionConfigKey) this._optionConfigKey$.next(this._optionConfigKey);
  }

  onChange(event: AnyObject) {
    const value = event.target.value.toLocaleString();

    const option = this.optionList.find(o => o.label.toLowerCase() === value);

    if (option) {
      this.labelSelected = option.label;
      this.valueSelected = option.value;
      this.imageSelected = option.image;
      this._changeValue(option);

      return;
    }

    const optionPrev = this.optionList.find(o => o.value === this.valueSelected);

    if (optionPrev && value.trim()) {
      this.labelSelected = '';
      setTimeout(() => this.labelSelected = optionPrev.label, 10);
      this._changeValue(optionPrev);

      return;
    }else{
      this.valueSelected = undefined;
      this.imageSelected = undefined;
      this.valueChange.emit(undefined);
    }
  }

  onSelect(option: FieldOption) {
    this.labelSelected = option.label;
    this.valueSelected = option.value;
    this.imageSelected = option.image;

    this._changeValue(option);
  }

  onKeyUp(event: AnyObject) {
    const search = (event?.target?.value || '').trim();
    const searchPrev = this._lastSearch.trim();

    this._lastSearch = search;

    if (searchPrev.trim() === search) return;

    this._search$.next(search);
    this._lastPressTime = new Date();
  }

  onFocus() {
    this._classSet.add('card-focus');
    this._search$.next('');
  }

  onBlur() {
    this._classSet.delete('card-focus');
  }

  private _consultOptions(search: string = ''): Observable<AnyObject> {
    const optionsUrl = this.optionsUrl;
    if (!optionsUrl?.trim()) {
      this._showOptions(search.toString());
      return of(null);
    }

    this.isLoading = true;
    return this._genericResource
      .getElements<FieldOption>(optionsUrl, {
        filter: { search, ...(this.filter || {}) },
        paginate: { page: 1, size: this.sizeResults }
      })
      .pipe(
        catchError(() => {
          return of({ objects: [] });
        }),
        tap(response =>
          this._showOptions(search.toString(), response?.objects as Array<FieldOption>)
        ),
        finalize(() => this.isLoading = false)
      );
  }

  private _showOptions(searchValue: string, asyncOptions?: FieldOption[]) {
    const { options } = this;
    const regex = new RegExp(searchValue.toString(), 'gi');
    const staticOptions = options?.filter(o => regex.test(o?.label));
    this.optionList = [...(staticOptions || []), ...(asyncOptions || [])];

  }

  private _changeValue(option: FieldOption) {
    this.valueChange.emit(option.value);
  }

  private _checkTimePress(): boolean {
    const { _lastPressTime } = this;
    if (!_lastPressTime) return false;

    return new Date().getTime() - _lastPressTime.getTime() > waitPress;
  }

  private _setOptionValue(value: string) {
    if (this.optionsUrl) return this._findInvert$.next(value);

    const allOptions = [...(this.optionList || []), ...(this._options || [])];
    const option = allOptions.find(o => o.value === value);

    if (!option) return;

    this.labelSelected = option.label;
    this.valueSelected = option.value;
    this.imageSelected = option.image;
  }

  private _findOptionFrom(value: string): Observable<FieldOption | null> {
    if (!this.optionsUrl) return of(null);

    return this._genericResource.getElements<FieldOption>(this.optionsUrl, {
      filter: { value, ...(this.filter || {}) },
      paginate: { page: 1, size: 1 }
    }).pipe(
      map(response => response?.objects[0])
    );
  }
}
